<template>
  <div class="blog-detail-container">
    <div class="blog-detail-card">
      <div v-if="blog" class="blog-content">
        <!-- Header Section -->
        <div class="blog-header">
          <div class="title-container">
            <h1
              class="blog-title"
              :class="{ 'mobile-title': $vuetify.breakpoint.xs }"
            >
              {{ blog.title }}
            </h1>
          </div>

          <!-- Author Info -->
          <div class="author-info">
            <editable-avatar
              :image="blog.poster_image"
              :editable="false"
              :size="$vuetify.breakpoint.xs ? 32 : 40"
              class="author-avatar"
              @click.native="goToAgencyProfile()"
            />

            <div class="author-details">
              <div class="author-name" @click.stop="goToAgencyProfile()">
                {{ blog.poster_name }}
              </div>
              <div class="post-date">
                Posted
                {{
                  new Date(this.blog.createdAt).toLocaleString("en-us", {
                    dateStyle: "long"
                  })
                }}
                <span class="read-time"
                  >• {{ calculateReadTime() }} min read</span
                >
              </div>
            </div>

            <v-spacer />

            <!-- Social Share with Dropdown -->
            <div class="social-share">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="primary"
                    outlined
                    small
                    v-bind="attrs"
                    v-on="on"
                    class="share-btn"
                  >
                    <v-icon left>mdi-share-variant</v-icon>
                    Share
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="shareOnTwitter">
                    <v-list-item-icon>
                      <v-icon color="#1DA1F2">mdi-twitter</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Twitter</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="shareOnFacebook">
                    <v-list-item-icon>
                      <v-icon color="#4267B2">mdi-facebook</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Facebook</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="shareOnLinkedIn">
                    <v-list-item-icon>
                      <v-icon color="#0077B5">mdi-linkedin</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>LinkedIn</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="copyToClipboard">
                    <v-list-item-icon>
                      <v-icon>mdi-content-copy</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Copy Link</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </div>

        <!-- Featured Image - Make it clickable -->
        <div
          class="featured-image-container"
          @click="openImageViewer(blog.cover)"
        >
          <div class="fixed-image-container">
            <v-img
              :src="blog.cover"
              :aspect-ratio="16 / 9"
              contain
              class="featured-image"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height" align="center" justify="center">
                  <v-progress-circular
                    indeterminate
                    color="grey lighten-5"
                  ></v-progress-circular>
                </v-row>
              </template>
              <div class="image-overlay"></div>
              <div class="image-caption" v-if="blog.imageCaption">
                {{ blog.imageCaption }}
              </div>
            </v-img>
          </div>
        </div>
        <!-- Table of Contents for long articles -->
        <!-- <div class="toc-container" v-if="showTOC">
         <div class="toc-header">
           <v-icon left>mdi-table-of-contents</v-icon>
           <span>Table of Contents</span>
         </div>
         <div class="toc-items">
           <a 
             v-for="(item, index) in tocItems" 
             :key="index" 
             :href="`#section-${index}`"
             class="toc-item"
           >
             {{ item }}
           </a>
         </div>
       </div> -->

        <!-- Blog Content -->
        <div class="blog-body ql-editor" v-html="blog.html"></div>

        <!-- Fullscreen Image Viewer Dialog -->
        <v-dialog
          v-model="dialog"
          max-width="95vw"
          content-class="image-viewer-dialog"
          @click:outside="dialog = false"
        >
          <div class="image-viewer-wrapper">
            <v-img
              :src="currentImage"
              contain
              :max-height="imageMaxHeight"
              :max-width="imageMaxWidth"
              class="viewed-image"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height" align="center" justify="center">
                  <v-progress-circular
                    indeterminate
                    color="grey lighten-5"
                  ></v-progress-circular>
                </v-row>
              </template>

              <v-btn
                icon
                dark
                absolute
                top
                right
                class="close-btn"
                @click="dialog = false"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-img>
          </div>
        </v-dialog>

        <!-- Tags -->
        <div class="blog-tags" v-if="blog.tags && blog.tags.length">
          <v-chip
            v-for="tag in blog.tags"
            :key="tag"
            small
            class="tag-chip"
            color="primary"
            text-color="white"
            @click="searchByTag(tag)"
          >
            <v-icon left small>mdi-tag</v-icon>
            {{ tag }}
          </v-chip>
        </div>

        <!-- Reactions Section -->
        <div class="reactions-section">
          <div class="reaction-stats">
            <blog-event-reaction-details
              :data="likeData"
              :onComment="showComments"
              :_id="$route.query._id"
            />
          </div>

          <div class="reaction-actions">
            <feed-like-board
              :onLike="onLike"
              :liked="likeData ? likeData.liked : null"
              class="like-board"
            />

            <v-btn
              text
              :color="commendable ? 'primary' : 'grey'"
              @click="toggleComments"
              class="comment-btn"
              :class="{ active: commendable }"
            >
              <v-icon left>mdi-comment-text-outline</v-icon>
              Comment
              <span
                v-if="likeData && likeData.comments > 0"
                class="comment-count"
              >
                ({{ likeData.comments }})
              </span>
            </v-btn>

            <v-btn
              icon
              @click="toggleBookmark"
              class="bookmark-btn"
              :color="isBookmarked ? 'amber' : 'grey'"
            >
              <v-icon>mdi-bookmark{{ isBookmarked ? "" : "-outline" }}</v-icon>
            </v-btn>

            <!-- Reading Progress Indicator -->
            <div class="reading-progress">
              <v-progress-linear
                :value="scrollProgress"
                height="6"
                color="primary"
                rounded
                class="progress-bar"
              ></v-progress-linear>
              <span class="progress-text"
                >{{ Math.round(scrollProgress) }}% read</span
              >
            </div>
          </div>
        </div>

        <!-- Comments Section -->
        <v-slide-y-transition>
          <div class="comments-section" v-show="commendable">
            <blog-post-comment
              ref="blogEventComment"
              :_id="$route.query._id"
              :onPosted="onPostedComment"
              :total="likeData ? likeData.comments : 0"
              :commentId="$route.query.comment_id"
            />
          </div>
        </v-slide-y-transition>

        <!-- Newsletter Subscription -->
        <div class="newsletter-card">
          <v-card class="newsletter-inner">
            <div class="newsletter-content">
              <v-icon large color="primary" class="newsletter-icon"
                >mdi-email-newsletter</v-icon
              >
              <h3 class="newsletter-title">Enjoyed this article?</h3>
              <p class="newsletter-text">
                Subscribe to our newsletter for more great content
              </p>
              <v-form
                @submit.prevent="subscribeNewsletter"
                class="newsletter-form"
              >
                <v-text-field
                  v-model="email"
                  label="Your email address"
                  type="email"
                  outlined
                  dense
                  required
                  class="email-input"
                ></v-text-field>
                <v-btn
                  type="submit"
                  color="primary"
                  class="subscribe-btn"
                  :loading="subscribing"
                >
                  Subscribe
                </v-btn>
              </v-form>
            </div>
          </v-card>
        </div>

        <!-- Related Posts -->
        <div class="related-posts" v-if="relatedBlogs.length">
          <h3 class="section-title">You might also like</h3>
          <div class="related-grid">
            <v-card
              v-for="related in relatedBlogs"
              :key="related._id"
              class="related-card"
              @click="goToBlog(related._id)"
            >
              <v-img
                :src="related.cover"
                height="150"
                class="related-image"
              ></v-img>
              <v-card-title class="related-title">
                {{ related.title }}
              </v-card-title>
              <v-card-subtitle class="related-date">
                {{ new Date(related.createdAt).toLocaleDateString() }}
                <span class="related-read-time"
                  >{{ calculateReadTime(related.html) }} min read</span
                >
              </v-card-subtitle>
            </v-card>
          </div>
        </div>

        <!-- Floating Action Buttons -->
        <div
          class="floating-actions"
          :class="{ scrolled: scrollPosition > 100 }"
        >
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                fab
                small
                color="primary"
                class="floating-btn"
                @click="scrollToTop"
                v-on="on"
              >
                <v-icon>mdi-arrow-up</v-icon>
              </v-btn>
            </template>
            <span>Back to top</span>
          </v-tooltip>

          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                fab
                small
                color="twitter"
                class="floating-btn"
                @click="shareOnTwitter"
                v-on="on"
              >
                <v-icon>mdi-twitter</v-icon>
              </v-btn>
            </template>
            <span>Share on Twitter</span>
          </v-tooltip>

          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                fab
                small
                color="facebook"
                class="floating-btn"
                @click="shareOnFacebook"
                v-on="on"
              >
                <v-icon>mdi-facebook</v-icon>
              </v-btn>
            </template>
            <span>Share on Facebook</span>
          </v-tooltip>
        </div>
      </div>

      <!-- Loading State -->
      <div v-else class="loading-state">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
          class="loading-spinner"
        />
        <div class="loading-text">Loading blog post...</div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <confirm-delete-dialog
      :deleteDialog="deleteDialog"
      :loading="loading"
      :onClose="onCloseDialog"
      :onConfirm="onConfirmDelete"
      title="Delete Blog Post"
      message="Are you sure you want to delete this blog post? This action cannot be undone."
    />

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.core.css";
import "quill/dist/quill.bubble.css";
import EditableAvatar from "@/components/EditableAvatar.vue";
import ConfirmDeleteDialog from "@/components/common/ConfirmDeleteDialog.vue";
import FeedLikeBoard from "../event_feeds/EventFeedLikeBoard.vue";
import BlogPostComment from "./BlogPostComment.vue";
import BlogEventReactionDetails from "./BlogReactionDetails.vue";

export default {
  components: {
    EditableAvatar,
    ConfirmDeleteDialog,
    FeedLikeBoard,
    BlogPostComment,
    BlogEventReactionDetails
  },
  name: "BlogDetails",
  data() {
    return {
      blog: null,
      relatedBlogs: [],
      deleteDialog: false,
      loading: false,
      likeData: null,
      commendable: false,
      isBookmarked: false,
      scrollPosition: 0,
      scrollProgress: 0,
      // Previous data properties remain
      imageViewer: {
        open: false,
        src: ""
      },
      dialog: false,
      currentImage: "",
      currentImageCaption: "",
      imageMaxHeight: "90vh",
      imageMaxWidth: "90vw",
      email: "",
      subscribing: false,
      snackbar: {
        show: false,
        message: "",
        color: "success"
      },
      tocItems: ["Introduction", "Main Content", "Conclusion"], // This would be dynamically generated
      showTOC: true,
      token: null,
    };
  },
  methods: {
    ...mapActions("blog", {
      getBlogDetails: "getUnAuthBlogDetails",
      deleteBlog: "deleteBlog",
      getLikeCount: "getLikeCount",
      like: "like",
      getRelatedBlogs: "getRelatedBlogs"
    }),
    calculateReadTime(html) {
      const content = html || this.blog?.html || "";
      const text = content.replace(/<[^>]*>/g, "");
      const wordCount = text.split(/\s+/).length;
      const wordsPerMinute = 200;
      return Math.ceil(wordCount / wordsPerMinute);
    },
    onEditBlog() {
      this.$router.push({ name: "edit-blog", query: { _id: this.blog._id } });
    },
            fetchToken() {
      this.token = localStorage.getItem("user-token");
      if (this.token) {
        //console.log("Token found:", this.token); 
      } else {
        console.log("Token not found in local storage."); 
      }
    },
    goToAgencyProfile() {
      this.$router.push({
        name: "agency-profile-details",
        params: { id: this.blog.poster }
      });
    },
    goToBlog(blogId) {
      this.$router.push({
        name: "blog-details",
        query: { _id: blogId },
        params: { fresh: true }
      });
    },
    onDeleteBlog() {
      this.deleteDialog = true;
    },
    onCloseDialog() {
      this.deleteDialog = false;
    },
    async onConfirmDelete() {
      this.loading = true;
      try {
        await this.deleteBlog({ _id: this.blog._id });
        this.$router.push({ name: "blog" });
        this.showSnackbar("Blog post deleted successfully", "success");
      } catch (error) {
        this.showSnackbar(
          error.response?.data?.message || "Failed to delete blog post",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    async loadData() {
      try {
        const blog = await this.getBlogDetails({ _id: this.$route.query._id });
        this.blog = blog;
        this.getLiked();
        this.loadRelatedBlogs();

        if (this.$route.query.comment_id) {
          this.commendable = true;
          this.$nextTick(() => {
            if (this.$refs.blogEventComment?.scrollToComment) {
              this.$refs.blogEventComment.scrollToComment(
                this.$route.query.comment_id
              );
            }
          });
        }

        this.checkBookmarkStatus();
      } catch (error) {
        this.showSnackbar("Failed to load blog post", "error");
        console.error(error);
      }
    },
    async loadRelatedBlogs() {
      try {
        if (this.blog?.tags?.length) {
          const related = await this.getRelatedBlogs({
            tags: this.blog.tags,
            exclude: this.blog._id,
            limit: 3
          });
          this.relatedBlogs = related;
        }
      } catch (error) {
        console.error("Failed to load related blogs", error);
      }
    },
    async getLiked() {
      try {
        this.likeData = await this.getLikeCount({ _id: this.$route.query._id });
      } catch (error) {
        console.error("Failed to load like data", error);
      }
    },
    async onLike(type) {
      try {
        if (this.token) {
            const data = await this.like({ _id: this.$route.query._id, type });
            this.likeData = { ...this.likeData, ...data };
            } else{
                        this.errorMessage = "Token not found";
        this.snackbar = true;
        this.$router.push({ name: "login" });
            }
      } catch (error) {
        this.showSnackbar(
          error.response?.data?.message || "Failed to process like",
          "error"
        );
      }
    },
    onPostedComment() {
      if (this.likeData) {
        this.likeData.comments = (this.likeData.comments || 0) + 1;
      } else {
        this.likeData = { ...this.likeData, comments: 1 };
      }
    },
    toggleComments() {
      this.commendable = !this.commendable;
      if (this.commendable) {
        this.$nextTick(() => {
          if (this.$refs.blogEventComment?.makeFocus) {
            this.$refs.blogEventComment.makeFocus();
          }
        });
      }
    },
    showComments() {
      this.commendable = true;
      this.$nextTick(() => {
        const commentsSection = document.querySelector(".comments-section");
        if (commentsSection) {
          commentsSection.scrollIntoView({ behavior: "smooth" });
        }
      });
    },
    toggleBookmark() {
      this.isBookmarked = !this.isBookmarked;
      this.showSnackbar(
        this.isBookmarked ? "Blog bookmarked" : "Bookmark removed",
        "success"
      );
    },
    shareOnTwitter() {
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        this.blog.title
      )}&url=${window.location.href}`;
      window.open(url, "_blank", "width=550,height=420");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        window.location.href
      )}`;
      window.open(url, "_blank", "width=550,height=420");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
        window.location.href
      )}&title=${encodeURIComponent(this.blog.title)}`;
      window.open(url, "_blank", "width=550,height=420");
    },
    copyToClipboard() {
      navigator.clipboard.writeText(window.location.href);
      this.showSnackbar("Link copied to clipboard", "success");
    },
    checkBookmarkStatus() {
      // Implement actual bookmark check logic here
      this.isBookmarked = Math.random() > 0.5;
    },
    handleScroll() {
      this.scrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;

      // Calculate scroll progress
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      this.scrollProgress = (scrollTop / (docHeight - windowHeight)) * 100;
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    },
    searchByTag(tag) {
      this.$router.push({
        name: "blog",
        query: { tag }
      });
    },
    showSnackbar(message, color) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    async subscribeNewsletter() {
      if (!this.email) {
        this.showSnackbar("Please enter a valid email address", "error");
        return;
      }

      this.subscribing = true;
      try {
        // Here you would call your newsletter subscription API
        // Simulating API call with timeout
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.showSnackbar("Thanks for subscribing!", "success");
        this.email = "";
      } catch (error) {
        this.showSnackbar("Failed to subscribe. Please try again.", "error");
      } finally {
        this.subscribing = false;
      }
    },
    openImageViewer(src, caption = "") {
      this.currentImage = src;
      this.currentImageCaption = caption;
      this.dialog = true;
      this.calculateMaxDimensions();
      window.addEventListener("resize", this.calculateMaxDimensions);
      // Optional: Adjust max dimensions based on viewport
    },
    downloadImage() {
      const link = document.createElement("a");
      link.href = this.currentImage;
      link.download = `image-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    calculateMaxDimensions() {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      // Adjust for margins and padding
      this.imageMaxHeight = `${windowHeight * 0.9}px`;
      this.imageMaxWidth = `${windowWidth * 0.9}px`;
    },
    closeImageViewer() {
      this.imageViewer.open = false;
    },

    // Modified featured image click handler
    onFeaturedImageClick() {
      if (this.blog?.cover) {
        this.openImageViewer(this.blog.cover);
      }
    }
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),
    processedHtml() {
      if (!this.blog?.html) return "";
      return this.blog.html.replace(/<img([^>]+)>/g, (match, attributes) => {
        // Extract src and alt from img tag
        const srcMatch = attributes.match(/src="([^"]*)"/);
        const altMatch = attributes.match(/alt="([^"]*)"/);
        const src = srcMatch ? srcMatch[1] : "";
        const alt = altMatch ? altMatch[1] : "";

        // Return img with onclick handler
        return `<img${attributes} style="cursor: zoom-in; max-width: 100%; height: auto;" 
                  onclick="event.preventDefault(); window.blogImageClick('${src}', '${alt}')">`;
      });
    }
  },
  watch: {
    "$route.query._id": function(newValue) {
      if (newValue) {
        this.blog = null;
        this.relatedBlogs = [];
        this.loadData();
      }
    }
  },
  mounted() {
    this.loadData();
    window.addEventListener("scroll", this.handleScroll);
    // Add global function to handle image clicks
    window.blogImageClick = (src, alt) => {
      this.openImageViewer(src, alt);
    };

    // Process images after content is rendered
    this.$nextTick(() => {
      const images = document.querySelectorAll(".blog-body img");
      images.forEach(img => {
        img.style.cursor = "zoom-in";
        img.addEventListener("click", e => {
          e.preventDefault();
          this.openImageViewer(img.src, img.alt);
        });
      });
    });
  },
      created() {
    this.fetchToken();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  }
};
</script>

<style lang="scss" scoped>
.blog-detail-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  position: relative;

  @media (max-width: 600px) {
    padding: 10px;
  }
}

.blog-detail-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  }
}

.blog-content {
  padding: 40px;
  position: relative;

  @media (max-width: 960px) {
    padding: 30px;
  }

  @media (max-width: 600px) {
    padding: 20px;
  }
}

.blog-header {
  margin-bottom: 30px;
}

.title-container {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.blog-title {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: #2c3e50;
  margin-right: 20px;
  flex-grow: 1;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #0a5b8a, #3498db);
    border-radius: 2px;
  }

  &.mobile-title {
    font-size: 1.8rem;

    &::after {
      width: 60px;
      height: 3px;
      bottom: -8px;
    }
  }
}

.action-buttons {
  display: flex;
  gap: 8px;

  .action-btn {
    transition: all 0.2s ease;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &.edit-btn {
      color: #0a5b8a;
      background-color: rgba(10, 91, 138, 0.1);
    }

    &.delete-btn {
      color: #e74c3c;
      background-color: rgba(231, 76, 60, 0.1);
    }
  }
}

.author-info {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 20px;

  @media (max-width: 600px) {
    flex-wrap: wrap;
  }
}

.author-avatar {
  cursor: pointer;
  transition: transform 0.3s ease;
  border: 2px solid #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
}

.author-details {
  .author-name {
    font-weight: 600;
    font-size: 1.1rem;
    color: #0a5b8a;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      color: #063a5a;
      text-decoration: underline;
    }
  }

  .post-date {
    font-size: 0.9rem;
    color: #7f8c8d;
    margin-top: 2px;
    display: flex;
    align-items: center;

    .read-time {
      margin-left: 8px;
      padding-left: 8px;
      position: relative;

      &::before {
        content: "•";
        position: absolute;
        left: 0;
      }
    }
  }
}

.social-share {
  display: flex;
  gap: 8px;

  .share-btn {
    border-radius: 20px;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
  }

  @media (max-width: 600px) {
    width: 100%;
    justify-content: flex-end;
  }
}

.featured-image-container {
  cursor: zoom-in;
  margin: 20px 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;

  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    //  background: linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%);
  }

  .image-viewer-content {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    background-color: rgba(0, 0, 0, 0.8);
  }
  .featured-image-container:hover {
    transform: scale(1.01);
  }
  .image-caption {
    position: absolute;
    bottom: 20px;
    left: 20px;
    right: 20px;
    color: white;
    font-size: 0.9rem;
    text-align: right;
    opacity: 0.9;
  }

  &.mobile-parallax {
    height: 250px !important;
  }
}

.toc-container {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin: 30px 0;
  border-left: 4px solid #0a5b8a;

  .toc-header {
    display: flex;
    align-items: center;
    font-weight: 600;
    margin-bottom: 12px;
    color: #2c3e50;
  }

  .toc-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .toc-item {
    color: #0a5b8a;
    text-decoration: none;
    padding: 6px 12px;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(10, 91, 138, 0.1);
      color: #063a5a;
      transform: translateX(4px);
    }
  }
}

.blog-body {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #34495e;
  position: relative;

  ::v-deep img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 20px 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.01);
    }
  }

  ::v-deep p {
    margin-bottom: 1.5em;
  }

  ::v-deep h2,
  ::v-deep h3 {
    margin-top: 1.5em;
    margin-bottom: 0.8em;
    color: #2c3e50;
    position: relative;
    padding-bottom: 8px;

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      width: 60px;
      height: 2px;
      background: linear-gradient(to right, #0a5b8a, #3498db);
    }
  }

  ::v-deep blockquote {
    border-left: 4px solid #0a5b8a;
    padding-left: 20px;
    margin: 20px 0;
    font-style: italic;
    color: #555;
    background: #f8f9fa;
    padding: 15px;
    border-radius: 0 4px 4px 0;
  }

  ::v-deep a {
    color: #0a5b8a;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      color: #063a5a;
      text-decoration: underline;
    }
  }
}

.blog-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 30px 0;

  .tag-chip {
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 4px 12px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
  }
}

.reactions-section {
  border-top: 1px solid #ecf0f1;
  border-bottom: 1px solid #ecf0f1;
  padding: 20px 0;
  margin: 30px 0;
}

.reaction-stats {
  margin-bottom: 20px;
}

/* Fixed container for featured image */
.centered-image {
  max-height: 80vh;
  max-width: 80vw;
  object-fit: contain;
  border-radius: 4px;
  // box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
}
.featured-image {
  width: 100%;
  background-color: #f5f5f5;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0.3) 100%
  );
}

.image-caption {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  color: white;
  text-align: center;
  padding: 8px 16px;
  background-color: rgba(0, 0, 0, 0.6);
}

/* Image Viewer Dialog Styles */
.image-viewer-dialog {
  box-shadow: none;
  background: transparent;
  overflow: visible;
}

.image-viewer-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.viewed-image {
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  background-color: rgba(0, 0, 0, 0.8);
}

.close-btn {
  background: rgba(0, 0, 0, 0.5) !important;
  margin: 10px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  transform: scale(1.1);
  background: rgba(0, 0, 0, 0.7) !important;
}

/* Responsive Adjustments */
@media (max-width: 600px) {
  .featured-image-container {
    margin: 10px 0;
  }

  .viewed-image {
    max-height: 95vh !important;
    max-width: 95vw !important;
  }
}

.fixed-image-container {
  width: 100%;
  height: 400px; /* Fixed height */
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.featured-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #fff;
}

.mobile-image {
  height: 250px !important;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgb(255 255 255 / 0%) 0%,
    rgba(0, 0, 0, 0.3) 100%
  );
}

.image-caption {
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  color: white;
  font-size: 0.9rem;
  text-align: center;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
}

@media (max-width: 600px) {
  .fixed-image-container {
    height: 250px;
  }
}

.reaction-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;

  .like-board {
    flex-grow: 1;
  }

  .comment-btn {
    transition: all 0.2s ease;
    border-radius: 20px;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;

    &.active {
      background-color: rgba(10, 91, 138, 0.1);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .comment-count {
      margin-left: 4px;
    }

    &:hover {
      transform: translateY(-2px);
    }
  }

  .bookmark-btn {
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
    }
  }

  .reading-progress {
    position: absolute;
    right: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-right: -36px;
    .progress-bar {
      width: 100px;
    }

    .progress-text {
      font-size: 0.8rem;
      color: #7f8c8d;
      min-width: 60px;
    }

    @media (max-width: 960px) {
      position: relative;
      margin-top: 10px;
      width: 100%;
      justify-content: flex-end;

      .progress-bar {
        width: 150px;
      }
    }

    @media (max-width: 600px) {
      display: none;
    }
  }

  @media (max-width: 960px) {
    flex-wrap: wrap;
  }
}

.comments-section {
  margin: 30px 0;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
}

.newsletter-card {
  margin: 40px 0;

  .newsletter-inner {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }

  .newsletter-content {
    padding: 30px;
    text-align: center;
    position: relative;
  }

  .newsletter-icon {
    margin-bottom: 15px;
  }

  .newsletter-title {
    font-size: 1.5rem;
    margin-bottom: 10px;
    color: #2c3e50;
  }

  .newsletter-text {
    color: #7f8c8d;
    margin-bottom: 20px;
    font-size: 1rem;
  }

  .newsletter-form {
    display: flex;
    gap: 10px;
    max-width: 500px;
    margin: 0 auto;

    .email-input {
      flex-grow: 1;

      ::v-deep .v-input__slot {
        background: white !important;
      }
    }

    .subscribe-btn {
      border-radius: 6px;
      text-transform: none;
      letter-spacing: normal;
      font-weight: 500;
    }

    @media (max-width: 600px) {
      flex-direction: column;

      .subscribe-btn {
        width: 100%;
      }
    }
  }
}

.related-posts {
  margin-top: 50px;

  .section-title {
    font-size: 1.5rem;
    margin-bottom: 20px;
    color: #2c3e50;
    position: relative;
    padding-bottom: 10px;

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      width: 60px;
      height: 3px;
      background: linear-gradient(to right, #0a5b8a, #3498db);
    }
  }

  .related-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }

  .related-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 8px;
    overflow: hidden;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    }

    .related-image {
      transition: transform 0.3s ease;
    }

    &:hover .related-image {
      transform: scale(1.03);
    }

    .related-title {
      font-size: 1.1rem;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .related-date {
      color: #7f8c8d;
      font-size: 0.85rem;
      display: flex;
      align-items: center;

      .related-read-time {
        margin-left: 8px;
        padding-left: 8px;
        position: relative;

        &::before {
          content: "•";
          position: absolute;
          left: 0;
        }
      }
    }
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px;

  .loading-spinner {
    margin-bottom: 20px;
  }

  .loading-text {
    font-size: 1.2rem;
    color: #7f8c8d;
  }
}

.floating-actions {
  position: fixed;
  right: 30px;
  bottom: 30px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  z-index: 100;
  transition: all 0.3s ease;
  opacity: 0;
  visibility: hidden;
  transform: translateY(20px);

  &.scrolled {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .floating-btn {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-3px) scale(1.1);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }

    &.twitter {
      background-color: #1da1f2 !important;
    }

    &.facebook {
      background-color: #4267b2 !important;
    }
  }

  @media (max-width: 600px) {
    right: 15px;
    bottom: 15px;
    gap: 10px;

    .floating-btn {
      width: 40px !important;
      height: 40px !important;
    }
  }
}

/* Featured Image Container */
.featured-image-container {
  cursor: zoom-in;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.01);
  }
}

/* Image Wrapper in Content */
::v-deep .image-wrapper {
  margin: 2rem 0;
  text-align: center;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.02);
    }
  }

  .image-caption {
    margin-top: 0.5rem;
    font-size: 0.9rem;
    color: #7f8c8d;
    font-style: italic;
  }
}

/* Full Screen Image Viewer */
.image-viewer-container {
  position: relative;
  height: 90vh;
  width: 90vw;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;

  .fullscreen-image {
    max-height: 100%;
    max-width: 100%;
    cursor: default;
  }

  .image-viewer-actions {
    position: absolute;
    top: 20px;
    right: 20px;
    display: flex;
    gap: 10px;

    .close-btn,
    .download-btn {
      background: rgba(255, 255, 255, 0.2);
      transition: all 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: scale(1.1);
      }
    }
  }
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .image-viewer-container {
    height: 80vh;
    width: 95vw;
  }

  ::v-deep .image-wrapper {
    margin: 1.5rem 0;

    img {
      border-radius: 4px;
    }
  }
}
</style>
